import { useEffect, useMemo, useState } from 'react'
import { X, Stethoscope, Landmark, FlaskConical, Lock, Coins, Building2, Info } from 'lucide-react'
import { cachedGet } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import { doctorCode, doctorFullName } from '../../lib/dentist.js'
import { relabelBranch } from '../../lib/asset.js'
import {
  fmtMoney, rowTotal, rowDF, rowLab, rowTrace, rowNonTrace, rowTransfer, rowPayout,
  isSettled, settledInfo, labBranch, n,
} from '../../lib/salaryDoctor.js'

/**
 * One doctor's month — read-only breakdown of a `salary_doctor` row.
 *
 * The point of this drawer is the ARITHMETIC: the stored `df_trace` /
 * `df_nontrace` are already net of the lab deduction, so a manager staring at the
 * table cannot tell why a fee shrank. Here the waterfall is spelled out in the
 * branch that actually applied (see lib/salaryDoctor.js#labBranch).
 */
export default function SalaryDetailDrawer({ row, doctor, title, period, onClose }) {
  const [branches, setBranches] = useState([])
  useEffect(() => {
    cachedGet('/api/branches')
      .then((r) => setBranches(Array.isArray(r) ? r : (r?.items || [])))
      .catch(() => {})
  }, [])

  const branchName = useMemo(() => {
    const m = new Map()
    for (const b of branches) m.set(Number(b.id), relabelBranch(b.name || '') || `สาขา #${b.id}`)
    return m
  }, [branches])

  const s = settledInfo(isSettled(row))
  const branch = labBranch(row)
  // `amount[]` is the per-branch split. gen2 computes it but does NOT persist it,
  // so only rows generated by the older DF_doctor/gen carry one — show the card
  // when it is there rather than pretending every row has it.
  const perBranch = Array.isArray(row?.amount) ? row.amount.filter(Boolean) : []
  const labs = Array.isArray(row?.labList) ? row.labList.filter(Boolean) : []

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-[520px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">

        <div className="px-5 py-3.5 bg-white border-b border-slate-200 flex items-start gap-3">
          <Avatar src={doctor?.profile_img} name={title || (doctor ? doctorCode(doctor) : '')} />
          <div className="min-w-0 flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900 leading-tight truncate">
              {title || (doctor ? doctorCode(doctor) : `หมอ #${row?.doctor_id}`)}
            </div>
            <div className="text-[12.5px] text-slate-500 truncate">
              {doctor
                ? doctorFullName(doctor)
                : [row?.doctor?.name, row?.doctor?.surname].filter(Boolean).join(' ')}
            </div>
            <div className="text-[12px] text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span>{period}</span>
              <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${s.tone}`}>
                {isSettled(row) && <Lock size={10} />} {s.label}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {/* headline */}
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
              <Coins size={12} /> สรุปรายได้
            </div>
            <Row k="รายได้รวมที่คนไข้จ่าย" v={fmtMoney(rowTotal(row))} />
            <Row k="ค่าตอบแทนหมอ (DF)" v={fmtMoney(rowDF(row))} strong />
            <Row k="ค่า Lab ที่หัก" v={rowLab(row) ? `−${fmtMoney(rowLab(row))}` : '—'} tone={rowLab(row) ? 'amber' : ''} />
            <div className="border-t border-slate-100 mt-2 pt-2">
              <Row k="โอนเข้าบัญชีในระบบ" v={fmtMoney(rowTrace(row))} />
              <Row k="โอนเข้าบัญชีนอกระบบ" v={fmtMoney(rowTransfer(row))} tone={rowTransfer(row) < 0 ? 'rose' : ''} />
              <Row k="รวมจ่ายทั้งหมด" v={fmtMoney(rowPayout(row))} strong />
            </div>
          </section>

          {/* the waterfall, in the branch that actually applied */}
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
              <FlaskConical size={12} /> การหักค่า Lab
            </div>
            {branch === 'none' && <p className="text-[13px] text-slate-500">เดือนนี้ไม่มีค่า Lab ที่ต้องหัก</p>}
            {branch === 'within' && (
              <p className="text-[13px] text-slate-600 leading-relaxed">
                ค่า Lab <b className="text-amber-700">{fmtMoney(rowLab(row))}</b> ถูกหักจาก DF นอกระบบทั้งหมด
                — เหลือโอนนอกระบบ <b>{fmtMoney(rowTransfer(row))}</b> · DF ในระบบ <b>{fmtMoney(rowTrace(row))}</b> ไม่ถูกกระทบ
              </p>
            )}
            {branch === 'into-trace' && (
              <p className="text-[13px] text-slate-600 leading-relaxed">
                ค่า Lab <b className="text-amber-700">{fmtMoney(rowLab(row))}</b> มากกว่า DF นอกระบบ
                — ยอดนอกระบบถูกหักจนหมด (โอน 0) และส่วนที่เหลือถูกหักต่อจาก <b>DF ในระบบ</b> ซึ่งเหลือ <b>{fmtMoney(rowTrace(row))}</b>
              </p>
            )}
            {branch === 'over' && (
              <p className="text-[13px] text-rose-700 leading-relaxed">
                ค่า Lab <b>{fmtMoney(rowLab(row))}</b> มากกว่า DF ทั้งเดือน <b>{fmtMoney(rowDF(row))}</b>
                — เดือนนี้ไม่มียอดโอน และหมอค้างชำระ <b>{fmtMoney(-rowTransfer(row))}</b>
              </p>
            )}
            {rowNonTrace(row) > 0 && branch !== 'none' && (
              <p className="text-[12px] text-slate-400 mt-1.5">DF นอกระบบคงเหลือหลังหัก: {fmtMoney(rowNonTrace(row))}</p>
            )}
            {labs.length > 0 && (
              <p className="text-[12px] text-slate-400 mt-2">
                งาน Lab ที่นำมาคิด {labs.length} รายการ (id: {labs.slice(0, 12).join(', ')}{labs.length > 12 ? ', …' : ''})
              </p>
            )}
          </section>

          {/* per-branch, when the row carries it */}
          {perBranch.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
                <Building2 size={12} /> แยกตามสาขา
              </div>
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-[11.5px] text-slate-400 border-b border-slate-100">
                    <th className="py-1.5">สาขา</th>
                    <th className="py-1.5 text-right">รายได้</th>
                    <th className="py-1.5 text-right">DF</th>
                    <th className="py-1.5 text-right">Lab</th>
                  </tr>
                </thead>
                <tbody>
                  {perBranch.map((b, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="py-1.5 text-slate-700">{branchName.get(Number(b.branch_id)) || `สาขา #${b.branch_id}`}</td>
                      <td className="py-1.5 text-right tabular-nums text-slate-600">{fmtMoney(n(b.total))}</td>
                      <td className="py-1.5 text-right tabular-nums text-slate-800">{fmtMoney(n(b.df))}</td>
                      <td className="py-1.5 text-right tabular-nums text-amber-700">{n(b.lab) ? `−${fmtMoney(n(b.lab))}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* payout accounts */}
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
              <Landmark size={12} /> บัญชีรับโอน
            </div>
            <Row k="บัญชีในระบบ" v={[row?.bank, row?.banknumber].filter(Boolean).join(' · ') || '—'} />
            <Row k="ยอดโอน (ในระบบ)" v={fmtMoney(rowTrace(row))} />
            <div className="border-t border-slate-100 mt-2 pt-2">
              <Row
                k="บัญชีนอกระบบ"
                v={[doctor?.bank_nontrack?.bank_name, doctor?.bank_nontrack?.bank_num].filter(Boolean).join(' · ') || 'ใช้บัญชีเดียวกัน'}
              />
              <Row k="ยอดโอน (นอกระบบ)" v={fmtMoney(rowTransfer(row))} />
            </div>
            <p className="text-[11.5px] text-slate-400 mt-2 flex items-start gap-1.5">
              <Info size={12} className="mt-0.5 shrink-0" />
              ไฟล์ ZIP ที่ดาวน์โหลดจะแยกเป็น 2 ไฟล์โอนเงิน (ในระบบ / นอกระบบ) ตามบัญชีด้านบน
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}

function Row({ k, v, strong, tone }) {
  const cls = tone === 'amber' ? 'text-amber-700' : tone === 'rose' ? 'text-rose-600' : (strong ? 'text-slate-900' : 'text-slate-700')
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-[13px] text-slate-500">{k}</span>
      <span className={`text-[13.5px] tabular-nums ${strong ? 'font-semibold' : ''} ${cls}`}>{v}</span>
    </div>
  )
}

function Avatar({ src, name }) {
  const [broken, setBroken] = useState(false)
  if (src && !broken) {
    return <img src={src} alt="" onError={() => setBroken(true)} className="w-11 h-11 rounded-full object-cover ring-1 ring-slate-200 shrink-0" />
  }
  return (
    <span className="w-11 h-11 rounded-full bg-brand-50 text-brand-600 text-[16px] font-semibold flex items-center justify-center shrink-0">
      {name ? nameInitial(name) : <Stethoscope size={18} />}
    </span>
  )
}
